import React, { useState, useEffect } from 'react';
import { 
  Button, Card, Typography, Container, TextField, 
  Box, Avatar, CardActionArea, Grid, Paper, IconButton
} from '@mui/material';
import { Link } from 'react-router-dom';
import { AddCircle, AccountBalance, Star, StarBorder } from '@mui/icons-material';
import { collection, getDocs, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';

function Dashboard({ user }) {
  const [bancos, setBancos] = useState([]);
  const [novoBanco, setNovoBanco] = useState('');
  const [privacidade, setPrivacidade] = useState(localStorage.getItem('ln_privacidade') === 'true');

  const API_BASE = process.env.REACT_APP_API_URL || '/api';

  const apiFetch = async (path, options = {}) => {
    const res = await fetch(`${API_BASE}${path}`, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
    });
    if (!res.ok) throw new Error(`API ${res.status}`);
    return res.json();
  };

  useEffect(() => {
    const checar = () => setPrivacidade(localStorage.getItem('ln_privacidade') === 'true');
    window.addEventListener('privacidadeAlterada', checar);
    const fetchData = async () => {
      try {
        // Tenta API
        const data = await apiFetch(`/bancos?userId=${user.uid}`);
        if (Array.isArray(data) && data.length >= 0) {
          setBancos(data);
          return;
        }
      } catch (err) {
        console.warn('API de bancos indisponível, usando Firestore. Erro:', err.message);
      }
      // Fallback Firestore
      const snap = await getDocs(collection(db, `users/${user.uid}/bancos`));
      setBancos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    fetchData();
    return () => window.removeEventListener('privacidadeAlterada', checar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.uid]);

  const adicionarBanco = async () => {
    if (!novoBanco) return;
    // 1) Tenta API
    try {
      await apiFetch('/bancos', {
        method: 'POST',
        body: JSON.stringify({ userId: user.uid, nome: novoBanco, saldo: 0 }),
      });
      const data = await apiFetch(`/bancos?userId=${user.uid}`);
      setBancos(data || []);
      setNovoBanco('');
      return;
    } catch (err) {
      console.warn('API de bancos indisponível ao criar, usando Firestore. Erro:', err.message);
    }
    // 2) Fallback Firestore
    await addDoc(collection(db, `users/${user.uid}/bancos`), { nome: novoBanco, saldo: 0, is_preferencial: false });
    setNovoBanco('');
    const snap = await getDocs(collection(db, `users/${user.uid}/bancos`));
    setBancos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const marcarPreferencial = async (bancoId) => {
    try {
      await apiFetch('/bancos/preferencial', {
        method: 'POST',
        body: JSON.stringify({ userId: user.uid, bancoId }),
      });
      const data = await apiFetch(`/bancos?userId=${user.uid}`);
      setBancos(data || []);
      return;
    } catch (err) {
      console.warn('API de bancos indisponível ao marcar preferencial, usando Firestore. Erro:', err.message);
    }
    // Fallback Firestore: define true para o escolhido e false para os demais
    const col = collection(db, `users/${user.uid}/bancos`);
    const snap = await getDocs(col);
    await Promise.all(
      snap.docs.map((d) =>
        updateDoc(doc(col, d.id), { is_preferencial: d.id === bancoId })
      )
    );
    const novoSnap = await getDocs(col);
    setBancos(novoSnap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 1, px: { xs: 0.5, sm: 2 } }}>
      {/* INTERFACE PARA NOVOS USUÁRIOS */}
      {bancos.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 3, bgcolor: '#e3f2fd' }}>
            <AccountBalance sx={{ fontSize: 40, color: '#1976d2' }} />
          </Avatar>
          <Typography variant="h5" fontWeight="bold" color="#1976d2" sx={{ mb: 2 }}>
            Bem-vindo ao seu Controle Financeiro!
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
            Comece adicionando sua primeira conta bancária para organizar suas finanças pessoais.
          </Typography>
          
          <Card sx={{ maxWidth: 400, mx: 'auto', p: 3, borderRadius: 3, boxShadow: 3 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Criar Primeira Conta
            </Typography>
            <TextField
              fullWidth
              label="Nome da Conta"
              placeholder="Ex: Conta Corrente, Poupança, etc."
              value={novoBanco}
              onChange={(e) => setNovoBanco(e.target.value)}
              sx={{ mb: 2 }}
              onKeyPress={(e) => e.key === 'Enter' && adicionarBanco()}
            />
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={adicionarBanco}
              disabled={!novoBanco.trim()}
              sx={{ 
                py: 1.5, 
                fontSize: '1.1rem',
                fontWeight: 'bold',
                borderRadius: 2
              }}
            >
              <AddCircle sx={{ mr: 1 }} />
              Criar Conta
            </Button>
          </Card>
        </Box>
      ) : (
        <>
          {/* INTERFACE PARA USUÁRIOS EXISTENTES */}
          <Paper elevation={0} sx={{ mb: 1.5, p: 1.5, display: 'flex', gap: 1, maxWidth: 500, bgcolor: 'transparent' }}>
            <TextField 
              size="small" 
              label="Nova Conta" 
              fullWidth 
              value={novoBanco} 
              onChange={(e) => setNovoBanco(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && adicionarBanco()}
            />
            <Button 
              variant="contained" 
              onClick={adicionarBanco} 
              sx={{ minWidth: 80 }}
              disabled={!novoBanco.trim()}
            >
              <AddCircle sx={{mr: 1}}/>
              Criar
            </Button>
          </Paper>

          {/* LISTA DE CONTAS EM GRADE RESPONSIVA */}
          <Grid container spacing={2}>
            {bancos.map(b => (
              <Grid item key={b.id} xs={6} sm={4} md={3}>
                <Card
                  sx={{ borderRadius: 2, border: '1px solid #e0e0e0', position: 'relative', overflow: 'hidden' }}
                  className="h-32 w-32 flex-shrink-0"
                >
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); marcarPreferencial(b.id); }}
                    sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2 }}
                  >
                    {b.is_preferencial ? <Star htmlColor="#fbbf24" fontSize="small" /> : <StarBorder fontSize="small" />}
                  </IconButton>
                  <CardActionArea 
                    component={Link} 
                    to={`/banco/${b.id}`} 
                    sx={{ 
                      height: '100%',
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 0.75
                    }}
                    className="h-32 w-32 text-center flex flex-col justify-center items-center"
                  >
                    <Avatar sx={{ width: 32, height: 32, mx: 'auto', bgcolor: '#f0f2f5' }}>
                      <AccountBalance color="primary" sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Typography 
                      variant="body2" 
                      fontWeight="bold" 
                      noWrap
                      className="truncate w-full"
                      sx={{ fontSize: '0.9rem', width: '100%', textOverflow: 'ellipsis', overflow: 'hidden' }}
                    >
                      {b.nome}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ color: b.saldo >= 0 ? 'green' : 'red', filter: privacidade ? 'blur(5px)' : 'none', fontSize: '0.95rem', fontWeight: 'bold' }}
                    >
                      R$ {b.saldo?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </Typography>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Container>
  );
}

export default Dashboard;
